import {VideoJsPlayer} from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import {setAdCredits} from 'app/helpers/adStepper';
import VideoEventProvider from 'app/components/tv/events/VideoEventProvider';
import AdFinishedEvent from 'app/components/tv/events/AdFinishedEvent';
import {captureSentryException} from 'app/services/sentryLogging';
import type {ImaOptions} from 'app/components/tv/models/ImaTypes';
import Video from 'app/components/tv/models/video';
import VideoAdsInitializedEvent from 'app/components/tv/events/VideoAdsInitializedEvent';
import Device from 'app/services/Device';
import AdKeywords from 'app/components/tv/models/AdKeywords';
import RequestAdsException from 'app/exceptions/RequestAdsException';

export type SetAdPlaying = (value: ((prevState: boolean) => boolean) | boolean) => void;
export type SetRemainingTime = (value: ((prevState: string | null) => string | null) | string | null) => void;

const AutoplayPreventedCode = 1205;
const NoVastResponseCode = 303;
const AdServerErrorCode = 1005;
const LoadingTimeoutCode = 402;

const MIN_AD_DURATION_IN_MS = 0;
const MAX_AD_DURATION_IN_MS = 30000;

export default class VideoAds {
  private readonly player: VideoJsPlayer;
  private wrapperDiv: HTMLElement | null = null;

  constructor(player: VideoJsPlayer) {
    this.player = player;
    this.initializeAdContainer = this.initializeAdContainer.bind(this);
    if (!this.player) {
      throw new Error('A Videojs-Player instance is required to setup video-ads.');
    }
  }

  setup(setAdPlaying: SetAdPlaying, setRemainingTime: SetRemainingTime, isFirstPage: boolean, ppid?: string): void {
    this.setupImaPlugin(setAdPlaying, setRemainingTime, ppid);
    this.player.on('adserror', this.handleAdsError);
    this.scheduleAdContainerInitialization(isFirstPage);
  }

  requestAds(followUpVideo: Video): void {
    const keywords = AdKeywords.fromVideo(followUpVideo);
    const url = `${process.env.SSR_ORIGIN_URL}${window.location.pathname}`;
    this.player.ima.changeAdTag(this.getAdTagUrl(keywords, url));
    try {
      this.player.ima.requestAds();
    } catch (e) {
      throw new RequestAdsException('Calling requestAds() failed somewhere inside videojs-ima code');
    }
  }

  dispose() {
    this.player.off('adserror', this.handleAdsError);
    this.player.ima.controller.sdkImpl.adDisplayContainer.destroy();
    this.player.ima.controller.reset();
  }

  private setupImaPlugin(setAdPlaying: SetAdPlaying, setRemainingTime: SetRemainingTime, ppid?: string) {
    const imaOptions: ImaOptions = {
      autoPlayAdBreaks: false,
      showCountdown: false,
      disableCustomPlaybackForIOS10Plus: true,
      locale: 'de',
      ppid: ppid,
      adsManagerLoadedCallback: () => this.handleAdsManagerLoadedCallback(setAdPlaying, setRemainingTime),
    };

    this.player.ima(imaOptions);
  }

  private scheduleAdContainerInitialization(isFirstPage: boolean): void {
    if (!isFirstPage) {
      this.initializeAdContainer();
      return;
    }
    this.removeControlsIPadAndAndroid();
    this.wrapperDiv = document.getElementById('content_video');
    this.addInitEventListeners();
  }

  private addInitEventListeners() {
    if (this.wrapperDiv) {
      this.wrapperDiv.addEventListener('click', this.initializeAdContainer);
      if (new Device().isTouchDevice()) {
        this.wrapperDiv.addEventListener('touchend', this.initializeAdContainer);
      }
    }
    window.addEventListener('keyup', this.initializeAdContainer);
  }

  private removeInitEventListeners(): void {
    if (this.wrapperDiv) {
      this.wrapperDiv.removeEventListener('click', this.initializeAdContainer);
      this.wrapperDiv.removeEventListener('touchend', this.initializeAdContainer);
    }
    window.removeEventListener('keyup', this.initializeAdContainer);
  }

  private initializeAdContainer(): void {
    this.player.ima.initializeAdDisplayContainer();
    this.removeInitEventListeners();
    this.dispatchInitializedEvent();
  }

  private dispatchInitializedEvent(): void {
    VideoEventProvider.getInstance().dispatch(new VideoAdsInitializedEvent());
  }

  private removeControlsIPadAndAndroid() {
    // Remove controls from the player on iPad and Android to stop native controls from stealing our click
    const contentPlayer = document.getElementById('content_video_html5_api');
    if (!contentPlayer) {
      throw new Error('No content player found');
    }
    const device = new Device();
    if ((device.isIPad() || device.isAndroid()) && contentPlayer.hasAttribute('controls')) {
      contentPlayer.removeAttribute('controls');
    }
  }

  private handleAdsManagerLoadedCallback = (setAdPlaying: SetAdPlaying, setRemainingTime: SetRemainingTime) => {
    this.player.on('playerresize', this.handleResize);

    this.player.ima.playAdBreak();

    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () =>
      this.handleContentResumeRequested(setAdPlaying)
    );
    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.AD_PROGRESS, event =>
      this.handleAdProgress(event, setRemainingTime)
    );
    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.STARTED, () => this.onAdStarted(setAdPlaying));
  };

  private handleResize = () => {
    const adsManager = this.player.ima.getAdsManager();
    const {width, height} = this.player.currentDimensions();
    adsManager.resize(width, height, window.google.ima.ViewMode.NORMAL);
  };

  private handleContentResumeRequested = (setAdPlaying: SetAdPlaying) => {
    this.player.off('playerresize', this.handleResize);
    setAdCredits();
    setAdPlaying(false);
    VideoEventProvider.getInstance().dispatch(new AdFinishedEvent());
  };

  private onAdStarted = (setAdPlaying: SetAdPlaying) => {
    setAdPlaying(true);
  };

  private handleAdProgress = (e: any, setRemainingTime: SetRemainingTime) => {
    const progressData = e.getAdData();
    if (progressData) {
      const countdown = progressData.adBreakDuration - Math.floor(progressData.currentTime);
      const remainingTime = countdown < 10 ? '0' + countdown.toString() : countdown.toString();
      setRemainingTime(remainingTime);
    }
  };

  private handleAdsError = (error: any) => {
    this.logAdError(error, this.player.currentSrc());
    setAdCredits(1);
    VideoEventProvider.getInstance().dispatch(new AdFinishedEvent());
  };

  private logAdError = (error: any, src: string): void => {
    const adError = error.data?.AdError;
    if (!adError) {
      captureSentryException(new Error('Ads Error without additional info'), {
        extra: {
          errorInfo: error,
          src,
        },
      });
      return;
    }
    const innerError = adError.getInnerError();
    const errorCode = adError.getErrorCode();
    if (innerError?.errorCode === AutoplayPreventedCode || errorCode === AutoplayPreventedCode) {
      return;
    }
    if (errorCode === NoVastResponseCode) {
      // expected error as there is no 100% fillrate
      return;
    }
    if (errorCode === AdServerErrorCode) {
      // there can always be an unexpected ad server error
      return;
    }
    if (errorCode === LoadingTimeoutCode) {
      // there can always be a timeout loading a video-ad
      return;
    }
    captureSentryException(new Error('Ads Error with additional info'), {
      extra: {
        errorInfo: {
          errorCode,
          innerError,
          vastErrorCode: adError.getVastErrorCode(),
          toString: adError.toString(),
        },
        src,
      },
    });
  };

  private getAdTagUrl(keywords: AdKeywords, url: string) {
    const customParams = encodeURIComponent(keywords.toParamString());
    const encodedRegionSlug = encodeURIComponent(keywords.region);
    const encodedUrl = encodeURIComponent(url);
    return `https://pubads.g.doubleclick.net/gampad/ads?iu=/27763518,1037284/FuPa_GmbH/fupa.net/${encodedRegionSlug}/PreRoll&description_url=${encodedUrl}&tfcd=0&npa=0&plcmt=1&sz=640x360&cust_params=${customParams}&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=&vad_type=linear&min_ad_duration=${MIN_AD_DURATION_IN_MS}&max_ad_duration=${MAX_AD_DURATION_IN_MS}`;
  }
}
